import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";

import styles from "@/styles/containers/command.module.scss";

import {
  TableRegular,
  UserCircle,
  Coin,
  GiftBlur,
  SavesLifes,
  Waiter,
} from "@/shared/components/Icons";

import { Button, Radio, Drawer, Loading, Tabs } from "@/shared/components";
import { Brand } from "@/shared/core/Brand";
import { Store } from "@/shared/core/Store";
import { Table, Table as TableCore } from "@/shared/core/Table";
import { Currency } from "@/shared/tools";
import { MoneyInput } from "@/shared/components/SimpleForm/CurrencyInput";
import InputMask from "react-input-mask";
import QRCode from "react-qr-code";

import { NetlogicOpenTableAPI } from "@/api/NetlogicOpenTableAPI";

import NetLogicCart from "../Cart/NetLogicCart";

import classNames from "classnames/bind";
import { OMath } from "@/shared/tools/OMath";
import { Customer } from "@/shared/core/Customer";
import Image from "next/image";
import { isDesktop, isMobile } from "react-device-detect";
const css = classNames.bind(styles);

const initialTabs = ["Pedidos por você", "Outros pedidos"];

const CALL_WAITER_OPTIONS = {
  CLOSE_COMMAND: "CLOSE_COMMAND",
  CALL_HELP: "CALL_HELP",
};

const PAYMENT_OPTIONS = {
  PAY_ALL: "PAY_ALL",
  PAY_CONSUME: "PAY_CONSUME",
  PAY_CUSTOM: "PAY_CUSTOM",
};

export default function TableNetlogic({ externalId, onClose }) {
  const [table, setTable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [selectedEnvironment, setSelectedEnvironment] = useState(null);
  const [openCallWaiter, setOpenCallWaiter] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("Pedidos por você");
  const [callWaiterOption, setCallWaiterOption] = useState(null);
  const [payment, setPayment] = useState(null);
  const [openPayment, setOpenPayment] = useState(false);
  const [payTax, setPayTax] = useState(true);
  const [customValue, setCustomValue] = useState(0);
  const [openEnterPhone, setOpenEnterPhone] = useState(false);
  const [openQrCode, setOpenQrCode] = useState(false);
  const [phone, setPhone] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [tableRef, setTableRef] = useState({});
  const [callingWaiter, setCallingWaiter] = useState(false);
  const [sentCallWaiter, setSentCallWaiter] = useState(false);
  const [openConfirmBack, setOpenConfirmBack] = useState(false);
  const [generateQrCode, setGenerateQrCode] = useState(false);
  const [openConfirmQrCode, setOpenConfirmQrCode] = useState(false);
  const [countDown, setCountDown] = useState(null);
  const [runTimer, setRunTimer] = useState(true);

  const seconds = String(countDown % 60).padStart(2, 0);
  const minutes = String(Math.floor(countDown / 60)).padStart(2, 0);

  useEffect(() => {
    if (countDown < 0) {
      onClose();
      setRunTimer(false);
    }
  }, [countDown, onClose]);

  useEffect(() => {
    let timerId;

    const startTimer = () => {
      setCountDown(60 * 10);
      timerId = setInterval(() => {
        setCountDown((countDown) => countDown - 1);
      }, 1000);
    };

    if (runTimer && openQrCode) startTimer();
    else clearInterval(timerId);

    return () => {
      clearInterval(timerId);
    };
  }, [runTimer, openQrCode]);

  useEffect(() => {}, []);

  const [netlogicConfiguration, setNetlogicConfiguration] = useState({});

  const router = useRouter();

  useEffect(() => {
    setSelectedEnvironment(Brand.alias ? Brand.alias : null);
  }, []);

  const environmentId = Store.id;
  const tableId = TableCore.id;

  const loadTable = useCallback(async () => {
    const tableDetailsReq = await NetlogicOpenTableAPI.getTableDetails(
      environmentId,
      tableId
    );
    const tableDetailsData = tableDetailsReq.getData({});

    if (tableDetailsData?.details?.menuItems) {
      const currentTable = localStorage.getItem("tableDetailsNetlogic");
      const tableReference = JSON.stringify({
        check_seq: tableDetailsData?.checkSeq,
        check_num: tableDetailsData?.checkNumber,
      });

      if (!currentTable || tableReference !== currentTable) {
        localStorage.setItem("tableDetailsNetlogic", tableReference);
      }

      if (currentTable && tableReference !== currentTable) {
        Customer.setCustomer(null);
      }

      setTableRef({
        check_seq: tableDetailsData?.checkSeq,
        check_num: tableDetailsData?.checkNumber,
      });
      setTable(tableDetailsData?.details);
    }
  }, [environmentId, tableId]);

  const onLoad = useCallback(async () => {
    setLoading(true);
    await loadTable();

    const netlogicConfiguration = Store.netlogic || {};
    setNetlogicConfiguration(netlogicConfiguration);

    setLoading(false);
    setPolling(true);
  }, [loadTable]);

  const goToCatalog = () => {
    if (isDesktop) onClose && onClose();
    if (isMobile) {
      const newPath = selectedEnvironment
        ? `/${selectedEnvironment}/cardapio`
        : "/cardapio";
      router.push(newPath);
    }
  };

  useEffect(() => {
    onLoad();
  }, [onLoad]);

  useEffect(() => {
    if (polling) {
      const timer = setInterval(async () => {
        await loadTable();
      }, 30000);
      return () => clearInterval(timer);
    }
  }, [loadTable, polling]);

  const manualServiceFee = table?.totals?.serviceChargeTotal || 0;
  const autoServiceFee = table?.totals?.autoServiceChargeTotal || 0;
  const serviceFee = +OMath.sum(manualServiceFee, autoServiceFee);

  const calculateTaxValue = (valueToPay) => {
    const total = table?.totals?.subtotal || 0;

    const taxPercentage = OMath.percentage(serviceFee, total);
    const taxValue = OMath.times(valueToPay, taxPercentage / 100);

    return taxValue;
  };

  const isMyOrder = (item) =>
    Customer.reference
      ? (item?.referenceText || "").startsWith(`${Customer.reference}`)
      : false;

  const myOrders = table?.menuItems?.filter(isMyOrder);

  const myOrdersValue = myOrders?.reduce(
    (acc, item) => acc + (item?.total ?? 0),
    0
  );

  const otherOrders = table?.menuItems?.filter((item) => !isMyOrder(item));

  const getValueToPay = () => {
    const subtotal = table?.totals?.subtotal || 0;
    const discount = OMath.abs(table?.totals?.subtotalDiscountTotal || 0);

    const selectedPayment = payment || PAYMENT_OPTIONS.PAY_ALL;

    let value = 0;

    switch (selectedPayment) {
      case PAYMENT_OPTIONS.PAY_ALL:
        value = OMath.sum(subtotal, discount);
        break;

      case PAYMENT_OPTIONS.PAY_CONSUME:
        value = myOrdersValue;
        break;

      case PAYMENT_OPTIONS.PAY_CUSTOM:
        value = OMath.sum(customValue, discount);
        break;

      default:
        value = OMath.sum(subtotal, discount);
        break;
    }

    if (payTax) {
      const taxPercentage = OMath.percentage(serviceFee, subtotal);
      const taxValue = OMath.times(value, taxPercentage / 100);
      const roundedTaxValue = OMath.round(taxValue);

      value = OMath.sum(value, roundedTaxValue);
    }

    return value || 0;
  };

  const valueToPay = getValueToPay();

  const handleGenerateQrCode = async (phone) => {
    const res = await fetch("/api/gerar-qrcode", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        nome: Customer.name || "",
        valor: Number.parseFloat(valueToPay) || "",
        comanda_id: externalId || "",
        telefone: phone ? phone.replace(/\D/g, "") : "",
        check_seq: tableRef.check_seq,
        check_num: tableRef.check_num,
      }),
    });

    const data = await res.json();

    setQrData(data);
  };

  const onCallWaiter = async () => {
    setCallingWaiter(true);

    try {
      await NetlogicOpenTableAPI.callWaiter(environmentId, tableId);
      setSentCallWaiter(true);
    } catch (err) {
      console.log(err);
    }
  };

  const cashbackProgram = JSON.parse(
    localStorage.getItem("cashbackProgram") || null
  );

  const getMaxCashback = () => {
    if (!cashbackProgram) return;
    const cashback = (valueToPay * cashbackProgram?.percentage) / 100;
    if (cashback > cashbackProgram?.max_gain) return cashbackProgram?.max_gain;
    return cashback;
  };

  const canCallWaiter = !!netlogicConfiguration?.callWaiterSku;

  return (
    <>
      {!loading && (
        <div className={styles.headerContainer}>
          <div className="d-flex align-center justify-between">
            <div className="d-flex align-center">
              <UserCircle />
              <div className="d-flex flex-col">
                <p className={styles.customerName}>
                  {Customer.name || "Cliente não identificado"}
                </p>
                {myOrders && (
                  <span className={styles.qtyOrders}>
                    {myOrders?.length === 0
                      ? "0 pedidos realizados"
                      : `${myOrders?.length} ${
                          myOrders?.length === 1
                            ? "pedido realizado"
                            : "pedidos realizados"
                        }`}
                  </span>
                )}
              </div>
            </div>
            <div className={styles.commandAndTable}>
              <span>{`Mesa ${externalId}`}</span>
            </div>
          </div>

          <Button
            design="primary"
            className="mt-20"
            onClick={() => setOpenCallWaiter(true)}
          >
            Chamar garçom
          </Button>
        </div>
      )}

      <div className={styles.tableScrollContainer}>
        {loading && (
          <div className={styles.loading}>
            <Loading fullScreen />
          </div>
        )}

        {!loading && table?.menuItems?.length && (
          <Tabs
            tabs={initialTabs}
            onTabSelected={(tab) => setActiveTab(tab)}
            activeTab={activeTab}
          />
        )}

        {!loading && !table?.menuItems?.length && (
          <div className={styles.empty}>
            <div className={styles.icon}>
              <TableRegular />
            </div>

            <p>Não há pedidos na sua mesa.</p>
            <span>
              O que acha de ver o nosso cardápio e experimentar uma das nossas
              opções?
            </span>

            <Button design="primary" onClick={() => goToCatalog()}>
              Ver cardápio
            </Button>
          </div>
        )}

        {!loading && table?.menuItems?.length > 0 && (
          <NetLogicCart
            myItems={myOrders}
            otherItems={otherOrders}
            activeTab={activeTab}
            goToCatalog={goToCatalog}
          />
        )}

        <Drawer open={!!error} alreadyDrawer direction="bottom-center">
          <p className={styles.alertTitle}>{error?.title}</p>
          <p className={styles.alertSubtitle}>{error?.description}</p>
          <Button
            design="primary"
            className="w-100"
            onClick={() => setError(null)}
          >
            Ok
          </Button>
        </Drawer>

        <Drawer
          open={openCallWaiter}
          setOpen={() => {
            setOpenCallWaiter(false);
            setCallWaiterOption(null);
          }}
          title="Chamar garçom"
          showBackButton
          withoutTransition
          withoutFade
          alreadyDrawer
          backButton={() => {
            setOpenCallWaiter(false);
            setCallWaiterOption(null);
          }}
        >
          <div className={styles.preCloseTableContainer}>
            <div
              className={css({
                option: true,
                selected:
                  callWaiterOption === CALL_WAITER_OPTIONS.CLOSE_COMMAND,
              })}
              onClick={() =>
                setCallWaiterOption(CALL_WAITER_OPTIONS.CLOSE_COMMAND)
              }
            >
              <Radio
                readOnly
                family="option"
                value={CALL_WAITER_OPTIONS.CLOSE_COMMAND}
                checked={callWaiterOption}
              />
              <div className={styles.icon}>
                <Coin />
              </div>
              <p className={styles.name}>Fechar conta</p>
            </div>

            {canCallWaiter && (
              <div
                className={css({
                  option: true,
                  selected: callWaiterOption === CALL_WAITER_OPTIONS.CALL_HELP,
                })}
                onClick={() =>
                  setCallWaiterOption(CALL_WAITER_OPTIONS.CALL_HELP)
                }
              >
                <Radio
                  readOnly
                  family="option"
                  value={CALL_WAITER_OPTIONS.CALL_HELP}
                  checked={callWaiterOption}
                />
                <div className={styles.icon}>
                  <SavesLifes />
                </div>
                <p className={styles.name}>Ajuda</p>
              </div>
            )}
          </div>

          {callWaiterOption && (
            <div className={styles.footer}>
              <Button
                design="primary"
                onClick={() => {
                  if (callWaiterOption === CALL_WAITER_OPTIONS.CLOSE_COMMAND)
                    setOpenPayment(true);

                  if (callWaiterOption === CALL_WAITER_OPTIONS.CALL_HELP)
                    onCallWaiter();
                }}
              >
                Continuar
              </Button>
            </div>
          )}
        </Drawer>

        <Drawer
          open={openPayment}
          setOpen={() => {
            setOpenPayment(false);
            setPayment(null);
          }}
          title="Facilite o seu pagamento"
          showBackButton
          alreadyDrawer
          backgroundColor="#F5F5F5"
          backButton={() => {
            setOpenPayment(false);
            setPayment(null);
          }}
        >
          <div className={styles.paymentContainer}>
            <div className={styles.tableTotal}>
              <div className={styles.total}>
                <p className={styles.name}>Total da mesa:</p>
                <p className={styles.value}>
                  {Currency.formatCurrency(
                    table?.totals?.subtotal +
                      Math.abs(table?.totals?.subtotalDiscountTotal || 0)
                  )}
                </p>
              </div>
              <div className={styles.total}>
                <p className={styles.name}>O que pedi:</p>
                <p className={styles.myValue}>
                  {Currency.formatCurrency(myOrdersValue)}
                </p>
              </div>
            </div>
            <div className={styles.options}>
              <p className={styles.title}>Como deseja pagar?</p>
              <div
                className={css({
                  option: true,
                  selected: payment === PAYMENT_OPTIONS.PAY_ALL,
                })}
                onClick={() => setPayment(PAYMENT_OPTIONS.PAY_ALL)}
              >
                <p className={styles.name}>Pagar tudo</p>
              </div>
              <div
                className={css({
                  option: true,
                  selected: payment === PAYMENT_OPTIONS.PAY_CONSUME,
                })}
                onClick={() => setPayment(PAYMENT_OPTIONS.PAY_CONSUME)}
              >
                <p className={styles.name}>Pagar o que consumi</p>
              </div>
              <div
                className={css({
                  option: true,
                  selected: payment === PAYMENT_OPTIONS.PAY_CUSTOM,
                })}
                onClick={() => setPayment(PAYMENT_OPTIONS.PAY_CUSTOM)}
              >
                <p className={styles.name}>Pagar valor personalizado</p>
              </div>
            </div>

            {[
              PAYMENT_OPTIONS.PAY_ALL,
              PAYMENT_OPTIONS.PAY_CONSUME,
              PAYMENT_OPTIONS.PAY_CUSTOM,
            ].includes(payment) && (
              <div className={styles.totals}>
                <p className={styles.title}>Valores</p>
                {payment === PAYMENT_OPTIONS.PAY_ALL && (
                  <ul>
                    <li>
                      <span style={{ color: "#3D734A", fontWeight: 500 }}>
                        Valor total da mesa
                      </span>
                      <span style={{ color: "#3D734A" }}>
                        {Currency.formatCurrency(
                          table?.totals?.subtotal +
                            Math.abs(table?.totals?.subtotalDiscountTotal || 0)
                        )}
                      </span>
                    </li>
                    {serviceFee && (
                      <li>
                        <span>Taxa de serviço</span>
                        <span>{Currency.formatCurrency(serviceFee)}</span>
                      </li>
                    )}
                    {table?.totals?.subtotalDiscountTotal ? (
                      <li>
                        <span>Total de descontos</span>
                        <span>
                          {Currency.formatCurrency(
                            table?.totals?.subtotalDiscountTotal
                          )}
                        </span>
                      </li>
                    ) : null}
                    <li>
                      <span>Total</span>
                      <span
                        style={{
                          fontSize: 14,
                          color: "#404040",
                          fontWeight: 600,
                        }}
                      >
                        {Currency.formatCurrency(valueToPay)}
                      </span>
                    </li>
                  </ul>
                )}
                {payment === PAYMENT_OPTIONS.PAY_CONSUME && (
                  <ul>
                    <li>
                      <span style={{ color: "#3D734A", fontWeight: 500 }}>
                        Valor total do seu pedido
                      </span>
                      <span style={{ color: "#3D734A" }}>
                        {Currency.formatCurrency(myOrdersValue)}
                      </span>
                    </li>
                    {serviceFee && (
                      <li>
                        <span>Taxa de serviço</span>
                        <span>
                          {Currency.formatCurrency(
                            calculateTaxValue(myOrdersValue)
                          )}
                        </span>
                      </li>
                    )}
                    {table?.totals?.subtotalDiscountTotal ? (
                      <li>
                        <span>Total de descontos</span>
                        <span>
                          {Currency.formatCurrency(
                            table?.totals?.subtotalDiscountTotal
                          )}
                        </span>
                      </li>
                    ) : null}
                    <li>
                      <span>Total</span>
                      <span
                        style={{
                          fontSize: 14,
                          color: "#404040",
                          fontWeight: 600,
                        }}
                      >
                        {Currency.formatCurrency(valueToPay)}
                      </span>
                    </li>
                  </ul>
                )}
                {payment === PAYMENT_OPTIONS.PAY_CUSTOM && (
                  <ul>
                    <li className="d-flex flex-col justify-start">
                      <span>Valor a ser pago</span>
                      <MoneyInput
                        name="change_for"
                        prefix="R$ "
                        thousandSeparator="."
                        decimalSeparator=","
                        currency={true}
                        id={"money-input"}
                        value={customValue}
                        onChange={(masked, raw) => {
                          setCustomValue(raw);
                        }}
                        className="mt-10"
                      />
                    </li>
                    {serviceFee && (
                      <li>
                        <span>Taxa de serviço</span>
                        <span>
                          {Currency.formatCurrency(
                            calculateTaxValue(customValue)
                          )}
                        </span>
                      </li>
                    )}
                    {table?.totals?.subtotalDiscountTotal ? (
                      <li>
                        <span>Total de descontos</span>
                        <span>
                          {Currency.formatCurrency(
                            table?.totals?.subtotalDiscountTotal
                          )}
                        </span>
                      </li>
                    ) : null}
                    <li>
                      <span>Total</span>
                      <span
                        style={{
                          fontSize: 14,
                          color: "#404040",
                          fontWeight: 600,
                        }}
                      >
                        {Currency.formatCurrency(valueToPay)}
                      </span>
                    </li>
                  </ul>
                )}
              </div>
            )}
          </div>

          {canCallWaiter && !payment && (
            <div className={styles.footer}>
              <div className="d-flex justify-center">
                <Button
                  onClick={() => {
                    onCallWaiter();
                    setGenerateQrCode(true);
                  }}
                >
                  Pular e chamar garçom
                </Button>
              </div>
            </div>
          )}

          {payment && (
            <div className={styles.footer}>
              <Button
                design="primary"
                onClick={() => {
                  if (cashbackProgram) {
                    setOpenEnterPhone(true);
                  } else {
                    setOpenConfirmQrCode(true);
                  }
                }}
                disabled={
                  payment === PAYMENT_OPTIONS.PAY_CUSTOM &&
                  (!customValue ||
                    customValue >
                      (payTax
                        ? table?.totals?.totalDue
                        : +OMath.sub(table?.totals?.totalDue, serviceFee)))
                }
              >
                Continuar
              </Button>
            </div>
          )}
        </Drawer>

        <Drawer
          open={openEnterPhone}
          onClose={() => setOpenEnterPhone(false)}
          withoutPadding
          alreadyDrawer
          backgroundColor="#59C071"
        >
          <div className={styles.cashbackContainer}>
            <button
              className={styles.skip}
              type="button"
              onClick={() => {
                setOpenConfirmQrCode(true);
                setPhone(null);
              }}
            >
              Pular
            </button>
            <div className="d-flex align-center justify-center">
              <GiftBlur />
            </div>
            <p className={styles.youWon}>Você ganhou</p>
            <p className={styles.value}>
              {Currency.formatCurrency(getMaxCashback())} de CASHBACK!
            </p>
            <span>
              Parabéns! Colete seu benefício
              <br />
              <strong>informando seu telefone abaixo.</strong>
            </span>

            <div className={styles.phoneContainer}>
              <p className={styles.phoneTitle}>Número de telefone</p>
              <InputMask
                name="phone"
                maskChar=" "
                value={phone}
                className={styles.phoneInput}
                placeholder="(00) 0 0000-0000"
                mask="(99) 99999-9999"
                type="tel"
                onChange={(e) => setPhone(e.target.value)}
              />

              <Button
                design="primary"
                onClick={() => {
                  setOpenConfirmQrCode(true);
                }}
                disabled={!phone || phone?.replace(/\D/g, "").length < 11}
                className="mt-30"
              >
                Coletar cashback
              </Button>
            </div>
          </div>
        </Drawer>

        <Drawer
          open={openQrCode}
          onClose={() => setOpenQrCode(false)}
          title="QR Code"
          alreadyDrawer
          backgroundColor="#F5F5F5"
        >
          <div className={styles.QrCodeContainer}>
            <div className={styles.QrCodeMessage}>
              <p>Atenção</p>
              <span>
                Apresente este QR Code para o garçom realizar o seu pagamento
              </span>
            </div>
            <p className={styles.time}>
              O QR Code expira em: ({minutes}:{seconds})
            </p>

            <div className="d-flex justify-center mt-10">
              {qrData && <QRCode value={JSON.stringify(qrData)} size={192} />}
            </div>
          </div>

          <div className={styles.customerTotal}>
            <p className={styles.value}>
              {Currency.formatCurrency(valueToPay)}
            </p>
            <div className="d-flex align-center justify-between">
              <span>Nome do cliente</span>
              <span>{Customer.name || "Cliente não identificado"}</span>
            </div>
          </div>

          <div className={styles.footer}>
            <div className="d-flex justify-center">
              <Button
                design="underline"
                onClick={() => setOpenConfirmBack(true)}
              >
                Recomeçar
              </Button>
            </div>
          </div>
        </Drawer>

        <Drawer
          open={callingWaiter}
          withoutPadding
          withoutTransition
          alreadyDrawer
        >
          <div className={styles.sendingCallWaiter}>
            <div className={styles.brandHeader}>
              {Brand.avatar && (
                <div className={styles.picture}>
                  <Image src={Brand.avatar} alt={Brand.name} layout="fill" />
                </div>
              )}
            </div>

            <div className={styles.statusContainer}>
              <p className={styles.msgOne}>
                Aguarde um pouco, estamos quase lá!
              </p>
            </div>

            <span className={styles.loader} />
          </div>
        </Drawer>

        <Drawer open={sentCallWaiter} alreadyDrawer direction="bottom-center">
          <div className="d-flex justify-center mb-20">
            <Waiter />
          </div>
          <p className={styles.alertTitle}>Um garçom foi chamado</p>
          <p className={styles.alertSubtitle}>
            Você será atendido em breve em sua mesa.
          </p>
          <Button
            design="primary"
            className="w-100"
            onClick={() => {
              setOpenCallWaiter(false);
              setSentCallWaiter(false);
              setCallingWaiter(false);

              if (generateQrCode) {
                handleGenerateQrCode();
                setOpenQrCode(true);
                setGenerateQrCode(false);
              }
            }}
          >
            Ok, entendi
          </Button>
        </Drawer>

        <Drawer
          open={openConfirmBack}
          onClose={(status) => setOpenConfirmBack(status)}
          direction="bottom"
          alreadyDrawer
        >
          <p className={styles.alertTitle}>
            Você tem certeza que deseja recomeçar?
          </p>
          <p className={styles.alertSubtitle}>
            Ao recomeçar, os pedidos vinculados à você perderão seu vínculo, e
            você entrará como um novo participante da mesa.
          </p>

          <Button
            design="primary"
            className="mb-10"
            onClick={() => setOpenConfirmBack(false)}
          >
            Continuar aqui
          </Button>
          <div className="d-flex justify-center mt-15">
            <Button
              onClick={() => {
                setOpenConfirmBack(false);
                setOpenQrCode(false);
                setOpenPayment(false);
                setOpenEnterPhone(false);
                setOpenCallWaiter(false);
                Customer.setCustomer(null);

                if (isDesktop) onClose && onClose();
                if (isMobile) router.push(`/${Table.brandAlias}/cardapio`);
              }}
            >
              Recomeçar
            </Button>
          </div>
        </Drawer>

        <Drawer
          open={openConfirmQrCode}
          onClose={(status) => setOpenConfirmQrCode(status)}
          direction="bottom"
          alreadyDrawer
        >
          <p className={styles.alertTitle}>
            Você tem certeza que deseja concluir seu pagamento?
          </p>
          <p className={styles.alertSubtitle}>Essa é uma ação sem retorno.</p>

          <Button
            design="primary"
            className="mb-10"
            onClick={() => {
              setOpenQrCode(true);
              handleGenerateQrCode(phone);
              setOpenEnterPhone(false);
              setOpenConfirmQrCode(false);
            }}
          >
            Concluir pagamento
          </Button>
          <div className="d-flex justify-center mt-15">
            <Button
              onClick={() => {
                setOpenConfirmQrCode(false);
              }}
            >
              Cancelar
            </Button>
          </div>
        </Drawer>
      </div>
    </>
  );
}
